// ============================================================
// 12. CustomerDebtReportPage.tsx
// ============================================================
// src/features/reports/pages/CustomerDebtReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getCustomerDebtReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { SummaryCard } from '../components/SummaryCard';
import {
    AlertTriangle, DollarSign, Users, TrendingUp,
    CreditCard, Phone, Calendar, Clock
} from 'lucide-react';
import { motion } from 'framer-motion';

export function CustomerDebtReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [showFilters, setShowFilters] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['report-customer-debt', tenantId],
        queryFn: () => getCustomerDebtReport(tenantId),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'customer_debt', format, {});
        window.open(url, '_blank');
    };

    const debtors = data?.debtors || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
            <ReportHeader
                title="Customer Debt Report"
                description="Outstanding customer balances"
                icon={AlertTriangle}
                iconGradient="from-red-500 to-rose-600"
                hasFilters={false}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || debtors.length === 0 ? (
                    <EmptyState
                        icon={<DollarSign className="w-10 h-10" />}
                        title="No Outstanding Debts"
                        description="All customers have cleared their balances. Great job!"
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Debtors"
                                value={String(data.summary?.totalDebtors || 0)}
                                icon={Users}
                                gradient="from-red-500 to-rose-600"
                            />
                            <SummaryCard
                                label="Total Debt"
                                value={formatNaira(data.summary?.totalDebt || 0)}
                                icon={DollarSign}
                                gradient="from-amber-500 to-orange-600"
                            />
                            <SummaryCard
                                label="Average Debt"
                                value={formatNaira(data.summary?.avgDebt || 0)}
                                icon={TrendingUp}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="High Risk (>₦100k)"
                                value={String(debtors.filter((d: any) => d.debtBalance > 100000).length)}
                                icon={AlertTriangle}
                                gradient="from-red-600 to-rose-700"
                            />
                        </div>

                        {/* Debt Distribution Visualization */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4">Debt Distribution</h3>
                                <div className="space-y-3">
                                    {debtors.slice(0, 10).map((debtor: any, i: number) => {
                                        const maxDebt = debtors[0]?.debtBalance || 1;
                                        const percentage = (debtor.debtBalance / maxDebt * 100);

                                        return (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="text-xs text-slate-500 w-24 truncate">{debtor.name}</span>
                                                <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${percentage}%` }}
                                                        transition={{ duration: 0.5, delay: i * 0.05 }}
                                                        className={`h-full rounded-full ${debtor.debtBalance > 100000 ? 'bg-gradient-to-r from-red-500 to-rose-600' :
                                                                debtor.debtBalance > 50000 ? 'bg-gradient-to-r from-amber-500 to-orange-600' :
                                                                    'bg-gradient-to-r from-blue-500 to-indigo-600'
                                                            }`}
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-slate-700 w-24 text-right">
                                                    {formatNaira(debtor.debtBalance)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="text-sm font-semibold text-slate-800">All Debtors</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Customer</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Phone</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Tier</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Debt Balance</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Total Spent</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Visits</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Last Visit</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Risk Level</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {debtors.map((debtor: any, i: number) => {
                                            const riskLevel = debtor.debtBalance > 100000 ? 'high' :
                                                debtor.debtBalance > 50000 ? 'medium' : 'low';

                                            return (
                                                <tr key={i} className={`hover:bg-slate-50 transition-colors ${riskLevel === 'high' ? 'bg-red-50/20' : ''
                                                    }`}>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${riskLevel === 'high' ? 'bg-red-100 text-red-700' :
                                                                    riskLevel === 'medium' ? 'bg-amber-100 text-amber-700' :
                                                                        'bg-blue-100 text-blue-700'
                                                                }`}>
                                                                <span className="text-xs font-bold">
                                                                    {debtor.name?.charAt(0) || '?'}
                                                                </span>
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-900">{debtor.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        <div className="flex items-center gap-1">
                                                            <Phone size={12} />
                                                            {debtor.phone}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge variant="info" size="sm">{debtor.loyaltyTier}</Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className={`text-sm font-bold ${riskLevel === 'high' ? 'text-red-600' :
                                                                riskLevel === 'medium' ? 'text-amber-600' :
                                                                    'text-slate-900'
                                                            }`}>
                                                            {formatNaira(debtor.debtBalance)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(debtor.totalSpent)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">
                                                        {formatNumber(debtor.visitCount)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        <div className="flex items-center gap-1">
                                                            <Clock size={12} />
                                                            {debtor.lastVisit ? new Date(debtor.lastVisit).toLocaleDateString() : 'Never'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge
                                                            variant={riskLevel === 'high' ? 'danger' : riskLevel === 'medium' ? 'warning' : 'success'}
                                                            size="sm"
                                                            pulse={riskLevel === 'high'}
                                                        >
                                                            {riskLevel.toUpperCase()}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}